import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import EmailIcon from "@mui/icons-material/Email";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";

import { SeverityColor, severityColorLabels, severityColors } from "../../SeveritySelect/severity.utils";
import SeveritySelect from "../../SeveritySelect/SeveritySelect";
import MetricRangeNamePicker, { type MetricRangeNamePickerProps } from "../RangeBar/MetricRangeNamePicker";

export type RangeColorNameEmailDeleteControlProps = {
  color: string;
  name: string;
  enrolledInEmail: boolean;
  canDelete?: boolean;
  disableName?: boolean;
  hideColorPicker?: boolean;
  nameControl?: ReactNode;
  nameErrorMessage?: string;
  onNameBlur?: MetricRangeNamePickerProps["onBlur"];
  onColorChange: (color: string) => void;
  onNameChange: (value: string) => void;
  onEmailChange: (value: boolean) => void;
  onDelete: () => void;
};

const getSeverityColor = (color: string): SeverityColor => {
  const normalizedColor = color.toLowerCase();
  const matchedSeverityColor = severityColors.find(
    (severityColor) =>
      severityColor.toLowerCase() === normalizedColor ||
      severityColorLabels[severityColor].toLowerCase() === normalizedColor,
  );

  return matchedSeverityColor ?? SeverityColor.GREEN;
};

const RangeColorNameEmailDeleteControl = ({
  color,
  name,
  enrolledInEmail,
  onColorChange,
  nameErrorMessage,
  onNameBlur,
  onNameChange,
  onEmailChange,
  onDelete,
  canDelete = false,
  disableName = false,
  hideColorPicker = false,
  nameControl,
}: RangeColorNameEmailDeleteControlProps) => {
  const { t } = useTranslation(["common"]);
  const severityColor = useMemo(() => getSeverityColor(color), [color]);

  const handleColorChange = useCallback(
    (newColor: SeverityColor) => {
      onColorChange(newColor);
    },
    [onColorChange],
  );

  //For when the user selects a predefined option in the free solo
  const handleNameChange = useCallback<MetricRangeNamePickerProps["onChange"]>(
    (_event, value) => {
      const name = typeof value === "string" ? value : value?.displayName ?? "";
      onNameChange(name);
    },
    [onNameChange],
  );

  //For when the user types whatever they want into the free solo
  const handleNameInputChange = useCallback<NonNullable<MetricRangeNamePickerProps["onInputChange"]>>(
    (_event, value, reason) => {
      if (reason !== "input" && reason !== "clear") return;

      onNameChange(value);
    },
    [onNameChange],
  );

  const handleEmailChange = useCallback(
    () => onEmailChange(!enrolledInEmail),
    [enrolledInEmail, onEmailChange],
  );

  return (
    <Box
      display="flex"
      gap={2}
      alignItems="center"
      sx={{
        "& .MuiInputBase-root": {
          alignItems: "center",
        },
      }}
    >
      {!hideColorPicker && (
        <Box
          width={160}
          flexShrink={0}
          alignItems="center"
          display="flex"
          sx={{ transform: "translateY(2px)" }}
        >
          <SeveritySelect fullwidth severityColor={severityColor} onChange={handleColorChange} />
        </Box>
      )}
      <Box minWidth={0} flexGrow={1} alignItems="center" display="flex">
        {nameControl ?? (
          <MetricRangeNamePicker
            value={name}
            onChange={handleNameChange}
            onInputChange={handleNameInputChange}
            disabled={disableName}
            error={Boolean(nameErrorMessage)}
            onBlur={onNameBlur}
            helperText={nameErrorMessage}
          />
        )}
      </Box>
      <Box display="flex" alignItems="center" flexShrink={0}>
        <Tooltip title={t("common:enroll_in_email_tooltip")}>
          <IconButton onClick={handleEmailChange}>
            <EmailIcon color={enrolledInEmail ? undefined : "disabled"} />
          </IconButton>
        </Tooltip>
      </Box>
      {canDelete && (
        <Box display="flex" alignItems="center" flexShrink={0}>
          <IconButton aria-label={t("common:delete_range")} onClick={onDelete}>
            <DeleteIcon />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default RangeColorNameEmailDeleteControl;
